<script lang="ts">
  export let title: string;
  export let hint: string | null = null;
  export let detail: string | null = null;

  // Computed once so the snapshot the user copies matches what they saw.
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const timestamp = new Date().toISOString();

  let copied = false;
  let copyTimer: ReturnType<typeof setTimeout> | null = null;

  async function copyDetails() {
    const blob = [
      detail ? `Error: ${detail}` : 'Error: (none)',
      `URL: ${currentUrl}`,
      `Time: ${timestamp}`
    ].join('\n');
    try {
      await navigator.clipboard.writeText(blob);
      copied = true;
      if (copyTimer) clearTimeout(copyTimer);
      copyTimer = setTimeout(() => (copied = false), 2000);
    } catch {
      // Clipboard can be blocked (permissions, insecure context); fail quietly.
    }
  }
</script>

<div class="error-panel" role="alert">
  <p class="error-panel__title">{title}</p>
  {#if hint}
    <p class="error-panel__hint">{hint}</p>
  {/if}

  {#if detail}
    <details class="error-panel__details">
      <summary>Technical details</summary>
      <pre class="error-panel__pre"><code>{detail}
{currentUrl}
{timestamp}</code></pre>
      <button type="button" class="ghost error-panel__copy" on:click={copyDetails}>
        {copied ? 'Copied ✓' : 'Copy details'}
      </button>
    </details>
  {/if}
</div>

<style>
  .error-panel {
    padding: var(--space-4);
    border: 1px solid var(--color-danger-500);
    border-radius: 0;
    background: rgba(255, 95, 86, 0.08);
    color: var(--color-text-secondary);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-normal);
  }

  .error-panel__title {
    margin: 0;
    font-weight: 700;
    color: var(--color-danger-500);
  }

  .error-panel__hint {
    margin: var(--space-2) 0 0;
    color: var(--color-text-secondary);
  }

  .error-panel__details {
    margin-top: var(--space-3);
  }

  .error-panel__details summary {
    cursor: pointer;
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
  }

  .error-panel__pre {
    margin: var(--space-3) 0 0;
    padding: var(--space-3);
    border: var(--border-subtle);
    background: var(--color-surface-950);
    overflow-x: auto;
  }

  .error-panel__pre code {
    display: block;
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    user-select: text;
  }

  .error-panel__copy {
    width: auto;
    margin-top: var(--space-3);
    padding: var(--space-1) var(--space-3);
    font-size: var(--font-size-xs);
  }
</style>
